// findingExplanations.js — Human-readable context for cryptic deep security findings
// Maps templateId to { title, explanation, impact, remediation, severityGuide }
// severityGuide is informational only — does NOT override actual finding severity

const FINDING_EXPLANATIONS = {
    // === Observed in production (painlesstechnology.com, March 2026) ===

    'testssl/overall_grade': {
        title: 'Overall SSL/TLS Grade',
        explanation: 'The overall SSL/TLS security grade assigned by testssl.sh. Grades range from A+ (best) to F (worst). Special grades: T = certificate not trusted, M = certificate name mismatch.',
        impact: 'A poor SSL grade indicates weak encryption configuration that may allow attackers to intercept or tamper with encrypted traffic. Grade M means the certificate does not match the hostname.',
        remediation: 'Review certificate coverage to ensure all hostnames are included. Disable weak protocols (TLS 1.0, 1.1) and cipher suites. Use modern TLS 1.2/1.3 configurations.',
        severityGuide: { 'A+': 'info', 'A': 'info', 'B': 'low', 'C': 'medium', 'D': 'high', 'F': 'critical', 'T': 'critical', 'M': 'critical' }
    },
    'testssl/early_data': {
        title: 'TLS 1.3 Early Data (0-RTT)',
        explanation: 'TLS 1.3 0-RTT early data is enabled. While it reduces latency, it allows replay attacks where an attacker can re-send captured TLS handshake data.',
        impact: 'An attacker who captures TLS handshake data can replay the early data portion, potentially re-executing sensitive operations like purchases or transfers.',
        remediation: 'Disable 0-RTT early data on the server. In Nginx: ssl_early_data off;'
    },
    'testssl/BREACH': {
        title: 'BREACH Compression Attack',
        explanation: 'The server uses HTTP compression (gzip, br) over HTTPS. The BREACH attack exploits this to extract secrets (CSRF tokens, session IDs) from compressed HTTPS responses.',
        impact: 'An attacker on the same network can extract secret values from HTTPS responses by making repeated requests and observing compressed response sizes.',
        remediation: 'Disable HTTP compression for responses containing secrets, separate secrets from user-controlled content, use per-request CSRF tokens, or add random padding.'
    },
    'testssl/cert_trust': {
        title: 'Certificate Trust Issue',
        explanation: 'The SSL certificate does not match the URI being tested, or the certificate chain is incomplete/untrusted.',
        impact: 'Browsers will show security warnings. An attacker could perform a man-in-the-middle attack since the server identity cannot be verified.',
        remediation: 'Obtain a certificate covering all hostnames. Use Subject Alternative Names (SANs) for multiple domains. Ensure the full certificate chain is installed.'
    },
    'testssl/ipv4_in_header': {
        title: 'Internal IPv4 Address Disclosure',
        explanation: 'The server leaks internal/private IPv4 addresses in HTTP response headers (commonly via Set-Cookie, X-Forwarded-For, or other headers).',
        impact: 'Attackers gain knowledge of internal IP addressing schemes, aiding targeted attacks against internal infrastructure.',
        remediation: 'Configure the web server and application to strip internal IP addresses from response headers. Review proxy configurations and cookie settings.'
    },
    'testssl/X-Frame-Options_multiple': {
        title: 'Multiple X-Frame-Options Headers',
        explanation: 'The server sends multiple, potentially conflicting X-Frame-Options headers. Browser behavior is undefined when encountering conflicts.',
        impact: 'Clickjacking protection may be ineffective. An attacker could embed the page in an iframe on a malicious site.',
        remediation: 'Ensure only one X-Frame-Options header is sent. Consider migrating to Content-Security-Policy frame-ancestors directive.'
    },
    'testssl/TLS1': {
        title: 'TLS 1.0 Protocol Status',
        explanation: 'Reports whether TLS 1.0 is offered. TLS 1.0 is deprecated (RFC 8996) due to BEAST and POODLE attacks. "Not offered" is the desired result.',
        impact: 'If offered: attackers can force a downgrade and exploit known weaknesses. PCI DSS requires TLS 1.0 disabled. If not offered: informational only.',
        remediation: 'Disable TLS 1.0. In Nginx: ssl_protocols TLSv1.2 TLSv1.3;'
    },

    // === Common testssl protocol findings ===

    'testssl/TLS1_1': {
        title: 'TLS 1.1 Protocol Status',
        explanation: 'Reports whether TLS 1.1 is offered. TLS 1.1 is deprecated (RFC 8996) and browsers dropped support in 2020.',
        impact: 'If offered: creates a downgrade attack surface. Violates PCI DSS 3.2+ requirements.',
        remediation: 'Disable TLS 1.1. Only allow TLS 1.2 and TLS 1.3.'
    },
    'testssl/TLS1_2': {
        title: 'TLS 1.2 Protocol Status',
        explanation: 'Reports whether TLS 1.2 is offered. TLS 1.2 is secure with strong cipher suites. "Offered" is the expected result.',
        impact: 'If not offered: older clients that do not support TLS 1.3 will be unable to connect.',
        remediation: 'Ensure TLS 1.2 is enabled with strong AEAD cipher suites (AES-GCM, ChaCha20).'
    },
    'testssl/TLS1_3': {
        title: 'TLS 1.3 Protocol Status',
        explanation: 'Reports whether TLS 1.3 is offered. TLS 1.3 is the latest and most secure version with mandatory forward secrecy.',
        impact: 'If not offered: clients miss improved security. Not a vulnerability, but a missed improvement.',
        remediation: 'Enable TLS 1.3 on the server. In Nginx: ssl_protocols TLSv1.2 TLSv1.3;'
    },
    'testssl/SSLv2': {
        title: 'SSLv2 Protocol (Obsolete)',
        explanation: 'Reports whether SSLv2 is offered. SSLv2 has fundamental design flaws (RFC 6176). "Not offered" is the only acceptable result.',
        impact: 'If offered: DROWN attack allows decryption of TLS connections using the SSLv2 key — critical even if clients never use SSLv2.',
        remediation: 'Disable SSLv2 immediately. This protocol should never be enabled on any production system.'
    },
    'testssl/SSLv3': {
        title: 'SSLv3 Protocol (Obsolete)',
        explanation: 'Reports whether SSLv3 is offered. SSLv3 is vulnerable to the POODLE attack (CVE-2014-3566).',
        impact: 'If offered: POODLE attack allows decryption of individual bytes of encrypted traffic.',
        remediation: 'Disable SSLv3 immediately. Use TLS 1.2 and TLS 1.3 only.'
    },

    // === Common testssl vulnerability findings ===

    'testssl/CRIME': {
        title: 'CRIME (TLS Compression Attack)',
        explanation: 'The server supports TLS-level compression. The CRIME attack exploits this to extract session cookies.',
        impact: 'An attacker can steal session cookies by observing compressed TLS record sizes, potentially hijacking sessions.',
        remediation: 'Disable TLS compression. Most modern servers disable it by default.'
    },
    'testssl/POODLE': {
        title: 'POODLE Attack Vulnerability',
        explanation: 'The server is vulnerable to POODLE (Padding Oracle On Downgraded Legacy Encryption), affecting SSLv3 and some TLS CBC ciphers.',
        impact: 'An attacker can decrypt individual bytes of encrypted HTTPS traffic by exploiting padding oracle behavior.',
        remediation: 'Disable SSLv3 and TLS CBC-mode ciphers. Use only AEAD cipher suites (AES-GCM, ChaCha20-Poly1305).'
    },
    'testssl/ROBOT': {
        title: 'ROBOT Attack (Bleichenbacher)',
        explanation: 'The server is vulnerable to Return Of Bleichenbacher\'s Oracle Threat, allowing decryption of RSA key exchange.',
        impact: 'An attacker can decrypt recorded TLS sessions that used RSA key exchange.',
        remediation: 'Disable RSA key exchange cipher suites. Use only ECDHE or DHE key exchange.'
    },
    'testssl/SWEET32': {
        title: 'SWEET32 Birthday Attack',
        explanation: 'The server supports 64-bit block cipher suites (3DES, Blowfish). SWEET32 exploits birthday-bound collisions.',
        impact: 'After capturing ~32GB of traffic, an attacker can recover plaintext from encrypted connections.',
        remediation: 'Disable 3DES and other 64-bit block ciphers. Use AES-128 or AES-256 with GCM mode.'
    },
    'testssl/LUCKY13': {
        title: 'LUCKY13 Timing Attack',
        explanation: 'The server may be vulnerable to the Lucky Thirteen timing attack on CBC-mode cipher suites.',
        impact: 'An attacker can potentially decrypt TLS traffic by observing precise timing differences in server responses.',
        remediation: 'Prefer AEAD cipher suites (AES-GCM, ChaCha20-Poly1305) over CBC-mode ciphers.'
    },
    'testssl/LOGJAM': {
        title: 'LOGJAM (Weak Diffie-Hellman)',
        explanation: 'The server supports DHE key exchange with parameters smaller than 2048 bits, enabling downgrade to export-grade DH.',
        impact: 'An attacker can intercept and decrypt HTTPS traffic by forcing a downgrade to weak DH parameters.',
        remediation: 'Use DHE parameters of at least 2048 bits, or switch to ECDHE key exchange.'
    },

    // === Common testssl header/certificate findings ===

    'testssl/HSTS': {
        title: 'HTTP Strict Transport Security',
        explanation: 'Reports HSTS header presence. HSTS instructs browsers to always use HTTPS, preventing protocol downgrade attacks.',
        impact: 'Without HSTS: users can be redirected to HTTP via man-in-the-middle attacks, exposing credentials and session cookies.',
        remediation: 'Add: Strict-Transport-Security: max-age=31536000; includeSubDomains; preload'
    },
    'testssl/X-Content-Type-Options': {
        title: 'X-Content-Type-Options Header',
        explanation: 'Reports whether X-Content-Type-Options: nosniff is set. Prevents browsers from MIME-type sniffing.',
        impact: 'Without this header: browsers may execute malicious scripts disguised as other file types.',
        remediation: 'Add the header: X-Content-Type-Options: nosniff'
    },
    'testssl/CSP': {
        title: 'Content Security Policy',
        explanation: 'Reports CSP header configuration. CSP restricts which resources the browser can load, defending against XSS.',
        impact: 'Without CSP: cross-site scripting attacks can load and execute arbitrary scripts.',
        remediation: 'Implement a Content-Security-Policy header. Start with report-only mode to identify issues.'
    },
    'testssl/cert_chain': {
        title: 'Certificate Chain Validation',
        explanation: 'Reports issues with the SSL certificate chain — missing intermediates or broken trust path.',
        impact: 'An incomplete chain causes connection failures in some clients, especially mobile browsers and API clients.',
        remediation: 'Install all intermediate certificates. Use SSL Labs or testssl.sh to verify the complete chain.'
    },
    'testssl/cert_expirationStatus': {
        title: 'Certificate Expiration Status',
        explanation: 'Reports whether the SSL certificate is expired, expiring soon, or valid.',
        impact: 'Expired certificates cause full-page browser warnings, destroying user trust and blocking functionality.',
        remediation: 'Renew before expiration. Set up automated certificate management (Let\'s Encrypt/certbot).'
    },
    'testssl/OCSP_stapling': {
        title: 'OCSP Stapling',
        explanation: 'Reports whether OCSP stapling is implemented. Allows the server to provide certificate revocation status directly.',
        impact: 'Without OCSP stapling: clients must query the CA separately, adding latency and privacy concerns.',
        remediation: 'Enable OCSP stapling. In Nginx: ssl_stapling on; ssl_stapling_verify on;'
    }
};

export default FINDING_EXPLANATIONS;
